import { useRuntimeVariables } from 'app-kit'
import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { useMemo } from 'react'

/**
 * The request payload for fetching a query response from Gecko Sage.
 */
export interface FetchSageQueryRequest {
  query: string
  context?: string
  organizationId?: string
  tags?: Record<string, string>
}

/**
 * The request payload for fetching a query response from Gecko Sage.
 */
export interface FetchVertexQueryRequest {
  query: string
  rolodexId?: string
  rolodexPath?: string
}

/**
 * The request payload for V2 LLM service with Pinecone RAG.
 */
export interface FetchV2LLMRequest {
  content: string
  rolodex_files?: Array<{
    id: string
    path: string
  }>
}

/**
 * The request payload for RAG Pinecone endpoint.
 */
export interface FetchRAGPineconeRequest {
  content: string
  organization_id: string
  additional_context?: string
  top_k?: number
  filters?: {
    [key: string]: string
  }
  rolodex_files?: Array<{
    id: string
    path: string
    mime_type?: string
  }>
}

/**
 * The request payload for fast uploading a file to Pinecone for RAG purposes.
 */
export interface FastUploadToPineconeRequest {
  organization_id: string
  file: File | Blob
  mime_type?: string | null
  tags?: {
    [key: string]: string
  }
}

/**
 * The request payload for checking if a file exists in Pinecone by tag.
 */
export interface CheckFileIdRequest {
  tag_name: string
  tag_value: string
  organization_id: string
}

export interface Citation {
  organization_id: string
  page_number: number
  report: string
}

/**
 * The response payload received from Gecko Sage after requesting a query.
 */
export interface FetchSageQueryResponse {
  result: string
  context: Array<Citation>
}

/**
 * The response payload received from Pinecone fast upload endpoint.
 */
export interface FastUploadToPineconeResponse {
  message?: string
}

/**
 * Represents an error object for Sage API interactions.
 */
export interface SageApiError {
  message: string
  status?: number
  details?: unknown
}

export function isSageApiErrorWithCode(
  error: unknown,
  message: string,
  statusCode: number | 404
): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const sageError = error as Partial<SageApiError>
  return (
    sageError.status === statusCode &&
    typeof sageError.details === 'string' &&
    sageError.details.includes(String(message))
  )
}

/**
 * Custom hook to interact with the Gecko Sage microservice for fetching AI-driven query responses.
 * @returns An object containing functions to interact with the Sage API.
 */
export function useSageService() {
  const { SAGE_URL, LLM_SERVICE } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()

  return useMemo(() => {
    /**
     * Fetches responses to queries from the Gecko Sage microservice.
     * @param requestPayload - The payload containing the query.
     * @returns A promise that resolves with the Sage query response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the Gecko Sage API URL is not configured.
     */
    const fetchQuery = async (
      requestPayload: FetchSageQueryRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!SAGE_URL) {
        console.error(
          'Gecko Sage API URL is not configured. Please set SAGE_URL in runtime variables.'
        )
        throw new Error('Gecko Sage API URL is not configured.')
      }

      // Construct URL with query parameter
      const url = new URL(`${SAGE_URL}/api/v1/sage/query_pump`)
      url.searchParams.append('query', requestPayload.query)

      try {
        const response: AxiosResponse<FetchSageQueryResponse> = await axios.get(
          url.toString(),
          {
            headers: {
              Accept: 'application/json',
            },
          }
        )
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching Sage query response.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'details' in responseData
            ? (responseData as { details: unknown }).details
            : undefined

        console.error('Error fetching Sage query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    const fetchVertexQuery = async (
      requestPayload: FetchVertexQueryRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'Gecko Sage API URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('Gecko Sage API URL is not configured.')
      }

      // Construct URL with query parameter
      const url = `${LLM_SERVICE}/api/v1/gemini/generate`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.query,
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching Vertex query response.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'details' in responseData
            ? (responseData as { details: unknown }).details
            : undefined

        console.error('Error fetching Vertex query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    const uploadFileToSage = async ({
      file,
      organization,
      metadata,
    }: {
      file: File
      organization: string
      metadata: Record<string, string>
    }) => {
      if (!SAGE_URL) {
        console.error(
          'Gecko Sage API URL is not configured. Please set SAGE_URL in runtime variables.'
        )
        throw new Error('Gecko Sage API URL is not configured.')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('organization_id', organization)
      formData.append('tags', JSON.stringify(metadata))
      try {
        const response: AxiosResponse<{ success: boolean; message: string }> =
          await axios.post(
            `${LLM_SERVICE}/api/v2/rag/pinecone/upload`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          )
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while uploading file to Sage.'
        console.error('Error uploading file to Sage:', {
          errorMessage,
          requestPayload: { file, organization, metadata },
        })
        throw new Error(errorMessage)
      }
    }

    const fetchQueryWithRAG = async (
      requestPayload: FetchSageQueryRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'Gecko Sage API URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('Gecko Sage API URL is not configured.')
      }
      if (!requestPayload.organizationId) {
        throw new Error('Organization is required for RAG fetch.')
      }
      // Construct URL with query parameter
      const url = `${LLM_SERVICE}/api/v2/rag/generate/pinecone`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.query,
            additional_context: requestPayload.context,
            organization_id: requestPayload.organizationId,
            filters: requestPayload.tags,
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching RAG query response.'
        console.error('Error fetching RAG query response:', {
          errorMessage,
          requestPayload,
        })
        throw new Error(errorMessage)
      }
    }

    /**
     * Fetches responses using V2 LLM service with Pinecone RAG.
     * @param requestPayload - The payload containing the query and context.
     * @returns A promise that resolves with the LLM query response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fetchV2LLMQuery = async (
      requestPayload: FetchV2LLMRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL for V2 API
      const url = `${LLM_SERVICE}/api/v2/gemini/generate`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.content,
            rolodex_files: requestPayload.rolodex_files || [],
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching V2 LLM query response.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'details' in responseData
            ? (responseData as { details: unknown }).details
            : undefined

        console.error('Error fetching V2 LLM query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Fetches responses using RAG Pinecone endpoint with organization and unit filtering.
     * @param requestPayload - The payload containing the query, filters, and context.
     * @returns A promise that resolves with the LLM query response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fetchRAGPineconeQuery = async (
      requestPayload: FetchRAGPineconeRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL for RAG Pinecone API
      const url = `${LLM_SERVICE}/api/v2/rag/generate/pinecone`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.content,
            filters: requestPayload.filters,
            additional_context: requestPayload.additional_context,
            organization_id: requestPayload.organization_id,
            top_k: requestPayload.top_k,
            rolodex_files: requestPayload.rolodex_files || [],
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching RAG Pinecone query response.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'detail' in responseData
            ? (responseData as { detail: unknown }).detail
            : undefined

        console.error('Error fetching RAG Pinecone query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Fast uploads a file to Pinecone for RAG purposes using algorithmic chunking.
     * @param requestPayload - The payload containing the file, organization ID, and optional metadata.
     * @returns A promise that resolves with the upload response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fastUploadToPinecone = async (
      requestPayload: FastUploadToPineconeRequest
    ): Promise<FastUploadToPineconeResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL for fast upload API
      const url = `${LLM_SERVICE}/api/v2/rag/pinecone/fast-upload`

      // Create FormData for file upload
      const formData = new FormData()
      formData.append('organization_id', requestPayload.organization_id)
      formData.append('file', requestPayload.file)

      if (requestPayload.mime_type !== undefined) {
        formData.append('mime_type', requestPayload.mime_type || '')
      }

      if (requestPayload.tags) {
        formData.append('tags', JSON.stringify(requestPayload.tags))
      }

      try {
        const response: AxiosResponse<FastUploadToPineconeResponse> =
          await axios.post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while uploading file to Pinecone.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'details' in responseData
            ? (responseData as { details: unknown }).details
            : undefined

        console.error('Error uploading file to Pinecone:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload: {
            organization_id: requestPayload.organization_id,
            mime_type: requestPayload.mime_type,
            tags: requestPayload.tags,
            file_name:
              requestPayload.file instanceof File
                ? requestPayload.file.name
                : 'Blob',
          },
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Checks if a chunk with the specified tag exists in the Pinecone database.
     * @param requestPayload - The payload containing tag name, tag value, and organization ID.
     * @returns A promise that resolves with the check response indicating if the file exists.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const checkFileId = async (
      requestPayload: CheckFileIdRequest
    ): Promise<boolean> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL with query parameters for check file ID API
      const url = new URL(`${LLM_SERVICE}/api/v2/rag/pinecone/find-file`)
      url.searchParams.append('tag_name', requestPayload.tag_name)
      url.searchParams.append('tag_value', requestPayload.tag_value)
      url.searchParams.append('organization_id', requestPayload.organization_id)

      try {
        const response: AxiosResponse<boolean> = await axios.get(
          url.toString(),
          {
            headers: {
              Accept: 'application/json',
            },
          }
        )
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while checking file existence in Pinecone.'
        const status = axiosError.response?.status

        // Cast to unknown first for type safety before trying to access details
        const responseData = axiosError.response?.data as unknown
        const errorDetails =
          typeof responseData === 'object' &&
          responseData !== null &&
          'details' in responseData
            ? (responseData as { details: unknown }).details
            : undefined

        console.error('Error checking file existence in Pinecone:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    return {
      fetchQuery,
      fetchVertexQuery,
      fetchQueryWithRAG,
      uploadFileToSage,
      fetchV2LLMQuery,
      fetchRAGPineconeQuery,
      fastUploadToPinecone,
      checkFileId,
    }
  }, [LLM_SERVICE, SAGE_URL, axios])
}
